import { useEffect } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || "G-PNP92XR5GE";

export const useGoogleAnalytics = (enabled: boolean = true) => {
  const location = useLocation();

  useEffect(() => {
    // Only run if GA is configured and enabled (consent given)
    if (!GA_MEASUREMENT_ID) return;

    // Initialize dataLayer and gtag function if not already done
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      
      // Set default consent state to denied (Consent Mode v2)
      window.gtag("consent", "default", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }

    // Only load script and grant consent when enabled
    if (!enabled) return;

    // Update consent to granted
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });

    // Load GA script if not already loaded
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`);
    if (!existingScript) {
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      // Wait for script to load before calling config
      script.onload = () => {
        window.gtag("js", new Date());
        window.gtag("config", GA_MEASUREMENT_ID, {
          send_page_view: false,
        });
        // Send initial page view with full details
        window.gtag("event", "page_view", {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      };
      
      document.head.appendChild(script);
    } else {
      // Script already exists, send page view
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [enabled]);

  // Track page views on route change (only when enabled)
  useEffect(() => {
    if (!enabled || !GA_MEASUREMENT_ID || !window.gtag) return;

    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location, enabled]);
};

// Helper to track custom events
export const trackEvent = (
  eventName: string,
  parameters?: Record<string, unknown>
) => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;
  window.gtag("event", eventName, parameters);
};
